// Calendar — month/week/day view of every dated event the retailer cares
// about (bookings, installs, deliveries, surveys, snags, payments, leads,
// follow-ups, team rota, personal). Lifted from the standalone portal but
// rebuilt as a clean module.
//
// Source-of-truth:
//   /var/www/kitchenos/portal-standalone.html
//     page section ............ line 2534 (#page-calendar)
//     KOS calendar engine ..... line 5625+ (kosCalRender, buildMonth/Week/Day)
//     legend / type colours ... line 25247+ (#kos-cal-legend, TYPES)
//     modal ................... line 4864 (#kos-cal-modal-overlay)
//
// Backend
// -------
// kitchenos-api currently exposes NO `/api/portal/calendar` endpoint
// (verified 2026-05 against /var/www/kitchenos-api/server.js — only
// `/api/portal/messages`, `/api/portal/contact-email`, `/api/portal/inbound-emails`
// and demo-login routes exist for the retailer). The legacy portal stored
// events in localStorage (`kos_calendar_events_v2`) and merged them with
// "smart events" derived in-browser from bookings/deliveries/etc.
//
// Strategy: fan out to the intended retailer endpoints (bookings,
// installations, deliveries, snags). Each one tolerates 404 (treated as
// "no data yet") so the page always renders. User-added events go through
// `/api/portal/calendar` (POST/GET) — when that 404s we keep them in memory
// so the UX still works. Backend wiring is a follow-up.

import { ApiError, API_BASE } from "@tkos/api-client";
import {
  createRepo,
  createSyncDot,
  createSyncQueue,
  createSyncStatus,
  iconMarkup,
  openDb,
  type Repo,
  type RepoConflictPayload,
  type SyncStatusController,
  type TkosDb,
} from "@tkos/shared-ui";

import { openEventDetail } from "../../shell/event-detail-modal";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// Raw fetch wrapper for the repo + sync queue. The queue needs the
// Response (status code matters for retry vs drop), which the typed
// ctx.api wrapper hides. Headers + credentials match api-client so
// nothing differs on the wire.
function rawApi(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
}

// Adapt the server envelope `{ events: [...] }` / `{ event: {...} }` to
// the bare records the repo expects.
function parseCalendarList(json: unknown): ServerCalRecord[] {
  const j = json as { events?: unknown } | null;
  if (!j || !Array.isArray(j.events)) return [];
  return (j.events as unknown[])
    .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
    .map((r) => toServerCalRecord(r));
}

function parseCalendarOne(json: unknown): ServerCalRecord | undefined {
  const j = json as { event?: unknown } | null;
  if (!j || !j.event || typeof j.event !== "object") return undefined;
  return toServerCalRecord(j.event as Record<string, unknown>);
}

interface ServerCalRecord {
  id: string;
  title: string;
  type: string;
  date: string;
  time: string;
  endTime: string;
  allDay: boolean;
  notes: string;
  customer: string;
  recurrence: string;
  /** Set only on events created from a customer-portal appointment request. */
  bookingId?: string | null;
  bookingStatus?: string | null;
}

function toServerCalRecord(r: Record<string, unknown>): ServerCalRecord {
  return {
    id: String(r["id"] ?? ""),
    title: String(r["title"] ?? ""),
    type: String(r["type"] ?? r["event_type"] ?? r["category"] ?? "booking"),
    date: String(r["date"] ?? r["event_date"] ?? ""),
    time: String(r["time"] ?? r["start_time"] ?? ""),
    endTime: String(r["endTime"] ?? r["end_time"] ?? ""),
    allDay: Boolean(r["allDay"] ?? r["all_day"] ?? false),
    notes: String(r["notes"] ?? ""),
    customer: String(r["customer"] ?? ""),
    recurrence: String(r["recurrence"] ?? "none"),
    bookingId: r["bookingId"] == null ? null : String(r["bookingId"]),
    bookingStatus: r["bookingStatus"] == null ? null : String(r["bookingStatus"]),
  };
}

// ── Event types & legend ────────────────────────────────────────────────────

/** All event categories shown in the legend (and selectable in the modal). */
type EventType =
  | "enquiry"
  | "delivery"
  | "booking"
  | "installation"
  | "personal"
  | "followup"
  | "survey"
  | "snag"
  | "payment"
  | "team"
  | "task";

interface TypeMeta {
  id: EventType;
  label: string;
  /** Solid colour (event chip background, legend dot). */
  color: string;
}

/**
 * Order matches the source-of-truth legend (portal-standalone.html:25258+),
 * which is in turn the order requested by the master spec. The colours come
 * from the same file — they are the user-customisable defaults.
 */
const TYPES: ReadonlyArray<TypeMeta> = [
  { id: "enquiry", label: "New Enquiries", color: "#e58200" },
  { id: "delivery", label: "Deliveries", color: "#3b82f6" },
  { id: "booking", label: "Bookings / Consultations", color: "#22c55e" },
  { id: "installation", label: "Installations", color: "#a855f7" },
  { id: "personal", label: "Personal", color: "#8a8f99" },
  { id: "followup", label: "Follow-ups", color: "#fbbf24" },
  { id: "survey", label: "Site Surveys", color: "#14b8a6" },
  { id: "snag", label: "Snags", color: "#f59e0b" },
  { id: "payment", label: "Payments", color: "#ef4444" },
  { id: "team", label: "Team", color: "#6366f1" },
  { id: "task", label: "To-Do Tasks", color: "#0891b2" },
];

const TYPE_BY_ID: Readonly<Record<EventType, TypeMeta>> = TYPES.reduce(
  (acc, t) => ({ ...acc, [t.id]: t }),
  {} as Record<EventType, TypeMeta>,
);

const VALID_TYPES = new Set<EventType>(TYPES.map((t) => t.id));

function coerceType(raw: unknown): EventType {
  if (typeof raw === "string" && VALID_TYPES.has(raw as EventType)) {
    return raw as EventType;
  }
  // Tolerate aliases used by other portal stores.
  if (typeof raw === "string") {
    const lc = raw.trim().toLowerCase();
    if (lc.includes("install") || lc.includes("fit")) return "installation";
    if (lc.includes("survey") || lc.includes("measure")) return "survey";
    if (lc.includes("snag")) return "snag";
    if (lc.includes("deliver")) return "delivery";
    if (lc.includes("payment") || lc.includes("invoice")) return "payment";
    if (lc.includes("follow")) return "followup";
    if (lc.includes("lead") || lc.includes("enquir") || lc.includes("inquiry")) {
      return "enquiry";
    }
    if (lc.includes("team") || lc.includes("staff")) return "team";
    if (lc.includes("personal")) return "personal";
    if (
      lc.includes("booking") ||
      lc.includes("appointment") ||
      lc.includes("consult") ||
      lc.includes("meeting")
    ) {
      return "booking";
    }
  }
  return "booking";
}

// ── Data shapes ─────────────────────────────────────────────────────────────

type CalView = "month" | "week" | "day";

interface CalEvent {
  id: string;
  title: string;
  type: EventType;
  /** YYYY-MM-DD (local). */
  date: string;
  /** HH:MM (24h) or "" if all-day. */
  time: string;
  /** HH:MM (24h) or "" if not specified. */
  endTime: string;
  allDay: boolean;
  notes: string;
  /** Optional customer link (free text — names from CRM in a future iteration). */
  customer: string;
  /** True when the event was synthesised from another portal source. */
  derived: boolean;
  /** Read-only when derived (you edit it from its source page, not here). */
  readOnly: boolean;
  /** Recurrence rule on the master row. Always 'none' on virtual instances. */
  recurrence: Recurrence;
  /** Id of the master row when this is a virtual recurrence instance.
   *  Allows the day list / detail popup to resolve the real row for
   *  delete. Null on master rows. */
  masterId: string | null;
  /** Booking this event represents, when it came from a customer-portal
   *  appointment request — lets the detail popup confirm it in place.
   *  Optional: only native events carry it, never the derived feeds. */
  bookingId?: string | null;
  bookingStatus?: string | null;
}

interface ServerEvent {
  id?: string | number;
  title?: string;
  type?: string;
  category?: string;
  date?: string;
  time?: string;
  endTime?: string;
  end_time?: string;
  allDay?: boolean;
  all_day?: boolean;
  notes?: string;
  customer?: string;
  recurrence?: string;
  /** Only present on events the API created from a customer-portal
   *  appointment request. Drives the popup's Confirm action. */
  bookingId?: string | null;
  bookingStatus?: string | null;
}

interface NewEventForm {
  title: string;
  type: EventType;
  date: string;
  time: string;
  endTime: string;
  allDay: boolean;
  notes: string;
  customer: string;
  recurrence: Recurrence;
}

type Recurrence = "none" | "daily" | "weekly" | "monthly" | "yearly";

// ── Helpers ─────────────────────────────────────────────────────────────────

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Format a Date as `YYYY-MM-DD` in the user's local time zone. */
function fmtYMD(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parse `YYYY-MM-DD` into a local-time Date (midnight). */
function parseYMD(s: string): Date {
  const [y, m, d] = s.split("-").map((p) => Number.parseInt(p, 10));
  return new Date(y || 1970, (m || 1) - 1, d || 1);
}

/** Format `HH:MM` (24h) → `9:30am` / `2:00pm`. */
function fmtTime12(t: string): string {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = Number.parseInt(hStr ?? "0", 10);
  const m = mStr ?? "00";
  if (!Number.isFinite(h)) return "";
  const ap = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${h}:${m}${ap}`;
}

/** Today's local YMD. */
function todayYMD(): string {
  return fmtYMD(new Date());
}

const monthYearFmt = new Intl.DateTimeFormat("en-GB", {
  month: "long",
  year: "numeric",
});
const dayLongFmt = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});
const weekRangeShortFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
});
const weekRangeLongFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const dowShortFmt = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
});

/** Mon-first week start for a given date (matches source-of-truth). */
function getWeekStart(d: Date): Date {
  const ws = new Date(d);
  const day = ws.getDay(); // 0=Sun
  const diff = (day + 6) % 7; // 0=Mon
  ws.setDate(ws.getDate() - diff);
  ws.setHours(0, 0, 0, 0);
  return ws;
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function esc(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function genId(): string {
  return `ev_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function normaliseEvent(raw: ServerEvent, fallbackType: EventType): CalEvent {
  const id = String(raw.id ?? genId());
  const title = String(raw.title ?? "").trim() || "(untitled)";
  const type = coerceType(raw.type ?? raw.category ?? fallbackType);
  const date = String(raw.date ?? "").trim();
  const time = String(raw.time ?? "").trim();
  const endTime = String(raw.endTime ?? raw.end_time ?? "").trim();
  const allDay = Boolean(raw.allDay ?? raw.all_day ?? !time);
  const notes = String(raw.notes ?? "").trim();
  const customer = String(raw.customer ?? "").trim();
  const bookingId = raw.bookingId == null ? null : String(raw.bookingId);
  const bookingStatus = raw.bookingStatus == null ? null : String(raw.bookingStatus);
  const rawRec = String(raw.recurrence ?? "none").toLowerCase();
  const recurrence: Recurrence =
    rawRec === "daily" || rawRec === "weekly" ||
    rawRec === "monthly" || rawRec === "yearly"
      ? rawRec
      : "none";
  return {
    id,
    title,
    type,
    date,
    time: allDay ? "" : time,
    endTime: allDay ? "" : endTime,
    allDay,
    notes,
    customer,
    derived: false,
    readOnly: false,
    recurrence,
    masterId: null,
    bookingId,
    bookingStatus,
  };
}

/**
 * Convert any datetime-ish value (epoch ms, ISO string) into a CalEvent
 * stub. Used to derive events from bookings/installs/deliveries/snags rows.
 */
function deriveFromRow(args: {
  id: string;
  title: string;
  type: EventType;
  when: unknown;
  notes?: string;
  customer?: string;
}): CalEvent | null {
  let ms: number | null = null;
  if (typeof args.when === "number" && Number.isFinite(args.when)) {
    ms = args.when;
  } else if (typeof args.when === "string" && args.when.trim()) {
    const parsed = Date.parse(args.when);
    if (Number.isFinite(parsed)) ms = parsed;
  }
  if (ms === null) return null;
  const d = new Date(ms);
  const date = fmtYMD(d);
  const allDay = d.getHours() === 0 && d.getMinutes() === 0;
  return {
    id: args.id,
    title: args.title || "(untitled)",
    type: args.type,
    date,
    time: allDay ? "" : `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    endTime: "",
    allDay,
    notes: args.notes ?? "",
    customer: args.customer ?? "",
    derived: true,
    readOnly: true,
    recurrence: "none",
    masterId: null,
  };
}

// ── Page ────────────────────────────────────────────────────────────────────

const calendarPage: Page = {
  name: "calendar",
  label: "Calendar",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();

    // ── Offline-first state ─────────────────────────────────────────────────
    // Calendar events live in IndexedDB (instant reads when offline,
    // queued writes when offline) and sync to PostgreSQL via the shared
    // sync queue. Pattern matches submissions / contacts.
    let db: TkosDb | null = null;
    let repo: Repo<ServerCalRecord> | null = null;
    let syncStatus: SyncStatusController | null = null;
    const offSubs: Array<() => void> = [];
    let disposed = false;

    // ── Local state ─────────────────────────────────────────────────────────

    let view: CalView = "month";
    /** Anchor date — any day in the displayed month (or week, or day). */
    let ref = new Date();
    /** YYYY-MM-DD or null. Click a date in month view to "drill in" to it. */
    let selected: string | null = null;

    // Consume router params so the dashboard's "+ Add event" and "Full
    // calendar ›" deep links land in the right place.
    const paramDate = ctx.params["date"];
    if (paramDate && /^\d{4}-\d{2}-\d{2}$/.test(paramDate)) {
      selected = paramDate;
      ref = parseYMD(paramDate);
    }
    const paramShouldAdd = ctx.params["add"] === "1";

    /** User-created events backed by /portal/calendar via the repo. */
    let userEvents: CalEvent[] = [];
    /** Events derived from other portal sources (bookings, installs, …). */
    let derivedEvents: CalEvent[] = [];
    /** Concatenation of the two, kept fresh by `recomputeAllEvents`. */
    let allEvents: CalEvent[] = [];

    /**
     * Expand recurring user events into virtual instances within a date
     * window. The window is centred on `ref` (the visible month cursor)
     * with a buffer either side so prev/next don't flash blank cells.
     * Masters with `recurrence: 'none'` pass through unchanged.
     */
    function expandRecurringEvents(masters: CalEvent[]): CalEvent[] {
      // Window: 6 months before the cursor through 24 months after.
      // Plenty for the visible 5-row month grid, generous enough that
      // any reasonable retailer-scoped recurring schedule renders.
      const winStart = new Date(ref.getFullYear(), ref.getMonth() - 6, 1);
      const winEnd = new Date(ref.getFullYear(), ref.getMonth() + 25, 0);
      const winStartT = winStart.getTime();
      const winEndT = winEnd.getTime();

      const out: CalEvent[] = [];
      for (const m of masters) {
        if (m.recurrence === "none") {
          out.push(m);
          continue;
        }
        // Walk dates from the master's start to the window end.
        const startD = parseYMD(m.date);
        if (!Number.isFinite(startD.getTime())) {
          out.push(m);
          continue;
        }
        const cursor = new Date(startD);
        // Hard cap to prevent runaway loops if something's malformed.
        let safety = 5000;
        while (cursor.getTime() <= winEndT && safety-- > 0) {
          if (cursor.getTime() >= winStartT) {
            const ymd = fmtYMD(cursor);
            const isMaster = ymd === m.date;
            out.push({
              ...m,
              id: isMaster ? m.id : `${m.id}::${ymd}`,
              date: ymd,
              masterId: isMaster ? null : m.id,
              // Virtual instances carry recurrence info so the detail
              // popup can still surface "Repeats weekly" etc. The
              // master's recurrence is preserved across copies.
            });
          }
          switch (m.recurrence) {
            case "daily":   cursor.setDate(cursor.getDate() + 1); break;
            case "weekly":  cursor.setDate(cursor.getDate() + 7); break;
            case "monthly": cursor.setMonth(cursor.getMonth() + 1); break;
            case "yearly":  cursor.setFullYear(cursor.getFullYear() + 1); break;
            default: safety = 0; break;
          }
        }
      }
      return out;
    }

    function recomputeAllEvents(): void {
      // De-dupe by id (user wins over derived if a clash ever happens).
      const byId = new Map<string, CalEvent>();
      for (const e of derivedEvents) byId.set(e.id, e);
      for (const e of userEvents) byId.set(e.id, e);
      const masters = Array.from(byId.values());
      allEvents = expandRecurringEvents(masters);
    }

    // ── DOM scaffold ────────────────────────────────────────────────────────

    const root = document.createElement("section");
    root.className = "cal-root";
    root.innerHTML = `
      <header class="cal-header">
        <div class="cal-header__titles">
          <h1 class="cal-title">Calendar</h1>
          <p class="cal-subtitle">Manage your schedule, installs, surveys &amp; appointments</p>
        </div>
        <button type="button" class="cal-add-btn" data-role="add-top">
          <span aria-hidden="true">+</span> Add Event
        </button>
      </header>

      <div class="cal-legend" data-role="legend" aria-label="Event type legend">
        <span class="cal-legend__label">Legend</span>
        ${TYPES.map(
          (t) => `
            <span class="cal-legend__item">
              <span class="cal-legend__dot" style="background:${t.color}"></span>
              ${esc(t.label)}
            </span>
          `,
        ).join("")}
      </div>

      <div class="cal-toolbar">
        <div class="cal-nav-group">
          <button type="button" class="cal-nav-btn" data-role="prev" aria-label="Previous">
            ${iconMarkup("chevron-left", { size: "sm" })}
          </button>
          <button type="button" class="cal-today-btn" data-role="today">Today</button>
          <button type="button" class="cal-nav-btn" data-role="next" aria-label="Next">
            ${iconMarkup("chevron-right", { size: "sm" })}
          </button>
        </div>
        <div class="cal-toolbar__title" data-role="title"></div>
        <div class="cal-toolbar__spacer"></div>
        <div class="cal-view-btns" role="tablist" aria-label="Calendar view">
          <button type="button" class="cal-view-btn" data-view="month" role="tab">Month</button>
          <button type="button" class="cal-view-btn" data-view="week"  role="tab">Week</button>
          <button type="button" class="cal-view-btn" data-view="day"   role="tab">Day</button>
        </div>
      </div>

      <div class="cal-state" data-role="state" aria-live="polite"></div>
      <div class="cal-grid"  data-role="grid"  aria-live="polite"></div>
      <section class="cal-day-list" data-role="day-list" hidden>
        <h2 class="cal-day-list__title" data-role="day-list-title"></h2>
        <div class="cal-day-list__items" data-role="day-list-items"></div>
      </section>
    `;
    container.appendChild(root);

    const titleEl = root.querySelector<HTMLDivElement>("[data-role='title']")!;
    const stateEl = root.querySelector<HTMLDivElement>("[data-role='state']")!;
    const gridEl = root.querySelector<HTMLDivElement>("[data-role='grid']")!;
    const dayListEl =
      root.querySelector<HTMLElement>("[data-role='day-list']")!;
    const dayListTitleEl = root.querySelector<HTMLHeadingElement>(
      "[data-role='day-list-title']",
    )!;
    const dayListItemsEl = root.querySelector<HTMLDivElement>(
      "[data-role='day-list-items']",
    )!;
    const prevBtn = root.querySelector<HTMLButtonElement>("[data-role='prev']")!;
    const nextBtn = root.querySelector<HTMLButtonElement>("[data-role='next']")!;
    const todayBtn =
      root.querySelector<HTMLButtonElement>("[data-role='today']")!;
    const addTopBtn = root.querySelector<HTMLButtonElement>(
      "[data-role='add-top']",
    )!;
    const viewBtns = Array.from(
      root.querySelectorAll<HTMLButtonElement>(".cal-view-btn"),
    );

    // ── Modal ───────────────────────────────────────────────────────────────

    const dialog = document.createElement("dialog");
    dialog.className = "cal-modal";
    dialog.innerHTML = `
      <form method="dialog" class="cal-modal__form" data-role="form" novalidate>
        <header class="cal-modal__head">
          <h2 data-role="modal-title">Add Event</h2>
          <button type="button" class="cal-modal__close" data-role="close" aria-label="Close">&times;</button>
        </header>
        <div class="cal-modal__body">
          <div class="cal-field">
            <label for="cal-title-input">Title</label>
            <input id="cal-title-input" name="title" type="text" placeholder="What is this event?" required />
          </div>
          <div class="cal-field">
            <label for="cal-type-input">Type</label>
            <select id="cal-type-input" name="type" required>
              ${TYPES.filter((t) => t.id !== "task").map(
                (t) => `<option value="${t.id}">${esc(t.label)}</option>`,
              ).join("")}
            </select>
          </div>
          <div class="cal-field">
            <label class="cal-checkbox">
              <input type="checkbox" data-role="all-day" />
              All-day event
            </label>
          </div>
          <div class="cal-field-row">
            <div class="cal-field">
              <label for="cal-date-input">Date</label>
              <input id="cal-date-input" name="date" type="date" required />
            </div>
            <div class="cal-field" data-role="time-field">
              <label for="cal-time-input">Start time</label>
              <input id="cal-time-input" name="time" type="time" />
            </div>
            <div class="cal-field" data-role="endtime-field">
              <label for="cal-endtime-input">End time <span class="cal-field__hint">(optional)</span></label>
              <input id="cal-endtime-input" name="endTime" type="time" />
            </div>
          </div>
          <div class="cal-field cal-picker" data-role="customer-picker">
            <label for="cal-customer-input">Customer <span class="cal-field__hint">(optional)</span></label>
            <div class="cal-picker__search-wrap" data-role="customer-picker-search-wrap">
              <input id="cal-customer-input"
                     name="customer"
                     type="text"
                     class="cal-picker__search"
                     data-role="customer-picker-search"
                     autocomplete="off"
                     role="combobox"
                     aria-autocomplete="list"
                     aria-expanded="false"
                     placeholder="Search customers by name…" />
              <div class="cal-picker__results"
                   role="listbox"
                   data-role="customer-picker-results"
                   hidden></div>
              <div class="cal-picker__hint" data-role="customer-picker-hint"></div>
            </div>
            <div class="cal-picker__selected" data-role="customer-picker-selected" hidden>
              <span class="cal-picker__chip" data-role="customer-picker-chip"></span>
              <button type="button" class="cal-picker__clear" data-role="customer-picker-clear">Change</button>
            </div>
          </div>
          <div class="cal-field">
            <label for="cal-recurrence-input">Repeat</label>
            <select id="cal-recurrence-input" name="recurrence">
              <option value="none">Does not repeat</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div class="cal-field">
            <label for="cal-notes-input">Notes <span class="cal-field__hint">(optional)</span></label>
            <textarea id="cal-notes-input" name="notes" rows="3" placeholder="Any additional details…"></textarea>
          </div>
        </div>
        <footer class="cal-modal__footer">
          <button type="button" class="cal-btn" data-role="cancel">Cancel</button>
          <button type="submit" class="cal-btn cal-btn--primary" data-role="save">Save event</button>
        </footer>
      </form>
    `;
    root.appendChild(dialog);

    const formEl = dialog.querySelector<HTMLFormElement>("[data-role='form']")!;
    const titleInput =
      dialog.querySelector<HTMLInputElement>("#cal-title-input")!;
    const typeInput =
      dialog.querySelector<HTMLSelectElement>("#cal-type-input")!;
    const dateInput =
      dialog.querySelector<HTMLInputElement>("#cal-date-input")!;
    const timeInput =
      dialog.querySelector<HTMLInputElement>("#cal-time-input")!;
    const endTimeInput =
      dialog.querySelector<HTMLInputElement>("#cal-endtime-input")!;
    // ── Customer picker (CRM-only, no freetext) ─────────────────────────────
    // Mirrors the compose-message modal pattern at pages/messages/page.ts.
    // Datasource: `/crm/contacts`. Selection is strict: the saved event's
    // `customer` field is the chosen contact's name. Empty selection means
    // "no customer linked" — the field is optional.
    interface CalContact { id: string; name: string }
    const customerSearch = dialog.querySelector<HTMLInputElement>(
      "[data-role='customer-picker-search']",
    )!;
    const customerSearchWrap = dialog.querySelector<HTMLDivElement>(
      "[data-role='customer-picker-search-wrap']",
    )!;
    const customerResults = dialog.querySelector<HTMLDivElement>(
      "[data-role='customer-picker-results']",
    )!;
    const customerSelectedEl = dialog.querySelector<HTMLDivElement>(
      "[data-role='customer-picker-selected']",
    )!;
    const customerChip = dialog.querySelector<HTMLSpanElement>(
      "[data-role='customer-picker-chip']",
    )!;
    const customerClearBtn = dialog.querySelector<HTMLButtonElement>(
      "[data-role='customer-picker-clear']",
    )!;
    const customerHint = dialog.querySelector<HTMLDivElement>(
      "[data-role='customer-picker-hint']",
    )!;

    let customerCache: CalContact[] | null = null;
    let customerSelection: CalContact | null = null;
    let customerHighlight = -1;

    const notesInput =
      dialog.querySelector<HTMLTextAreaElement>("#cal-notes-input")!;
    const allDayInput = dialog.querySelector<HTMLInputElement>(
      "[data-role='all-day']",
    )!;
    const recurrenceInput = dialog.querySelector<HTMLSelectElement>(
      "#cal-recurrence-input",
    )!;
    const timeFieldEl = dialog.querySelector<HTMLDivElement>(
      "[data-role='time-field']",
    )!;
    const endTimeFieldEl = dialog.querySelector<HTMLDivElement>(
      "[data-role='endtime-field']",
    )!;
    const saveBtn =
      dialog.querySelector<HTMLButtonElement>("[data-role='save']")!;
    const cancelBtn =
      dialog.querySelector<HTMLButtonElement>("[data-role='cancel']")!;
    const closeBtn =
      dialog.querySelector<HTMLButtonElement>("[data-role='close']")!;

    function syncAllDayUI(): void {
      const allDay = allDayInput.checked;
      timeFieldEl.classList.toggle("cal-field--hidden", allDay);
      endTimeFieldEl.classList.toggle("cal-field--hidden", allDay);
      if (allDay) {
        timeInput.value = "";
        endTimeInput.value = "";
      }
    }

    function resetForm(prefilledDate?: string): void {
      titleInput.value = "";
      typeInput.value = "booking";
      dateInput.value = prefilledDate ?? todayYMD();
      timeInput.value = "10:00";
      endTimeInput.value = "";
      clearCustomerSelection();
      notesInput.value = "";
      allDayInput.checked = false;
      recurrenceInput.value = "none";
      syncAllDayUI();
    }

    function openModal(prefilledDate?: string): void {
      resetForm(prefilledDate);
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
      window.setTimeout(() => titleInput.focus(), 60);
      // Load the CRM contact list lazily on first open; refresh on every
      // subsequent open so the dropdown stays current without blocking.
      void refreshCustomerCache();
    }

    function closeModal(): void {
      if (dialog.open) dialog.close();
    }

    // ── Customer picker wiring ──────────────────────────────────────────────

    function setCustomerHint(text: string): void {
      customerHint.textContent = text;
    }

    function escPickerAttr(s: string): string {
      return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    function selectCustomer(c: CalContact): void {
      customerSelection = c;
      customerSearchWrap.hidden = true;
      customerResults.hidden = true;
      customerSearch.setAttribute("aria-expanded", "false");
      customerSelectedEl.hidden = false;
      customerChip.textContent = c.name;
      setCustomerHint("");
    }

    function clearCustomerSelection(): void {
      customerSelection = null;
      customerSearch.value = "";
      customerSearchWrap.hidden = false;
      customerSelectedEl.hidden = true;
      customerResults.hidden = true;
      customerResults.innerHTML = "";
      customerSearch.setAttribute("aria-expanded", "false");
      setCustomerHint("");
      customerHighlight = -1;
    }

    function renderCustomerResults(query: string): void {
      const list = customerCache ?? [];
      const q = query.trim().toLowerCase();

      if (!q && document.activeElement !== customerSearch) {
        customerResults.innerHTML = "";
        customerResults.hidden = true;
        customerSearch.setAttribute("aria-expanded", "false");
        setCustomerHint(
          list.length === 0
            ? "No customers yet — add one in the CRM first."
            : `${list.length} customer${list.length === 1 ? "" : "s"} available — start typing to search.`,
        );
        customerHighlight = -1;
        return;
      }

      const matches = q
        ? list.filter((c) => c.name.toLowerCase().includes(q))
        : list;
      const visible = matches.slice(0, 20);

      if (visible.length === 0) {
        customerResults.innerHTML = "";
        customerResults.hidden = true;
        customerSearch.setAttribute("aria-expanded", "false");
        setCustomerHint(
          list.length === 0
            ? "No customers yet — add one in the CRM first."
            : "No matches — try a different name.",
        );
        customerHighlight = -1;
        return;
      }

      customerHighlight = 0;
      customerResults.innerHTML = visible
        .map((c, i) => {
          const safeName = escPickerAttr(c.name);
          return `
            <button type="button" class="cal-picker__option${i === customerHighlight ? " is-active" : ""}"
                    role="option" data-cid="${escPickerAttr(c.id)}"
                    aria-selected="${i === customerHighlight ? "true" : "false"}">
              <span class="cal-picker__option-name">${safeName}</span>
            </button>
          `;
        })
        .join("");
      customerResults.hidden = false;
      customerSearch.setAttribute("aria-expanded", "true");
      setCustomerHint(
        matches.length > visible.length
          ? `Showing ${visible.length} of ${matches.length} — keep typing to narrow.`
          : "",
      );
    }

    async function refreshCustomerCache(): Promise<void> {
      setCustomerHint("Loading…");
      try {
        interface WireCustomer { id?: string; name?: string }
        const resp = await ctx.api<{ contacts?: WireCustomer[] }>(
          "/crm/contacts",
          { signal: ac.signal },
        );
        const list = (Array.isArray(resp?.contacts) ? resp.contacts : [])
          .map((r): CalContact => ({
            id: String(r.id ?? "").trim(),
            name: String(r.name ?? "").trim(),
          }))
          .filter((r) => r.id && r.name);
        customerCache = list;
        // "Auto-populates if only one match exists" — when the whole CRM
        // has exactly one contact, pre-select it on modal open.
        if (list.length === 1 && !customerSelection) {
          const only = list[0];
          if (only) {
            selectCustomer(only);
            return;
          }
        }
        renderCustomerResults(customerSearch.value);
      } catch (err) {
        if (!isAbortError(err)) {
          setCustomerHint("Could not load customers. Try again shortly.");
        }
      }
    }

    function applyCustomerHighlight(els: NodeListOf<HTMLButtonElement>): void {
      els.forEach((el, i) => {
        el.classList.toggle("is-active", i === customerHighlight);
        el.setAttribute("aria-selected", i === customerHighlight ? "true" : "false");
        if (i === customerHighlight) el.scrollIntoView({ block: "nearest" });
      });
    }

    customerSearch.addEventListener("input", () => {
      renderCustomerResults(customerSearch.value);
    });
    customerSearch.addEventListener("focus", () => {
      renderCustomerResults(customerSearch.value);
    });
    customerSearch.addEventListener("keydown", (e: KeyboardEvent) => {
      const optionEls = customerResults.querySelectorAll<HTMLButtonElement>(".cal-picker__option");
      if (optionEls.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        customerHighlight = (customerHighlight + 1) % optionEls.length;
        applyCustomerHighlight(optionEls);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        customerHighlight = (customerHighlight - 1 + optionEls.length) % optionEls.length;
        applyCustomerHighlight(optionEls);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const chosen = optionEls[customerHighlight] ?? optionEls[0];
        chosen?.click();
      } else if (e.key === "Escape") {
        if (!customerResults.hidden) {
          e.preventDefault();
          customerResults.hidden = true;
          customerSearch.setAttribute("aria-expanded", "false");
        }
      }
    });
    customerResults.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(".cal-picker__option");
      if (!btn) return;
      const cid = btn.dataset["cid"];
      const match = (customerCache ?? []).find((c) => c.id === cid);
      if (match) selectCustomer(match);
    });
    customerClearBtn.addEventListener("click", () => {
      clearCustomerSelection();
      setTimeout(() => customerSearch.focus(), 0);
    });
    // Click outside the picker (but still inside the modal) closes the
    // results dropdown without clearing the search text.
    dialog.addEventListener("click", (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest(".cal-picker")) return;
      if (!customerResults.hidden) {
        customerResults.hidden = true;
        customerSearch.setAttribute("aria-expanded", "false");
      }
    });

    // ── Event detail dialog ─────────────────────────────────────────────────
    // Shared shell module (`shell/event-detail-modal.ts`) owns the
    // view/edit/delete popup. We pass `onSave` / `onDelete` callbacks so
    // writes route through the offline-first repo (preserving optimistic
    // local writes + the sync queue) rather than hitting /api directly.

    function openDetail(ev: CalEvent): void {
      openEventDetail(
        {
          id: ev.id,
          title: ev.title,
          type: ev.type,
          date: ev.date,
          time: ev.time,
          endTime: ev.endTime,
          allDay: ev.allDay,
          notes: ev.notes,
          customer: ev.customer,
          recurrence: ev.recurrence,
          derived: ev.derived,
          readOnly: ev.readOnly,
          masterId: ev.masterId,
          bookingId: ev.bookingId ?? null,
          bookingStatus: ev.bookingStatus ?? null,
        },
        {
          showToast: ctx.showToast,
          onSave: async (masterId, patch) => {
            await updateEvent(masterId, patch as Partial<ServerCalRecord>);
          },
          onDelete: async (masterId) => {
            await deleteEvent(masterId);
          },
        },
      );
    }

    // ── Render ──────────────────────────────────────────────────────────────

    function setTitleForView(): void {
      let s = "";
      if (view === "month") {
        s = monthYearFmt.format(ref);
      } else if (view === "week") {
        const ws = getWeekStart(ref);
        const we = new Date(ws);
        we.setDate(we.getDate() + 6);
        s = `${weekRangeShortFmt.format(ws)} – ${weekRangeLongFmt.format(we)}`;
      } else {
        s = dayLongFmt.format(ref);
      }
      titleEl.textContent = s;
    }

    function setActiveViewBtn(): void {
      for (const btn of viewBtns) {
        btn.classList.toggle("is-active", btn.dataset.view === view);
        btn.setAttribute(
          "aria-selected",
          btn.dataset.view === view ? "true" : "false",
        );
      }
    }

    function renderMonth(): void {
      const year = ref.getFullYear();
      const month = ref.getMonth();
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      const startOffset = (first.getDay() + 6) % 7; // Mon=0
      const today = todayYMD();

      const grid = document.createElement("div");
      grid.className = "cal-month";

      const dowRow = document.createElement("div");
      dowRow.className = "cal-month__dow-row";
      for (const d of ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]) {
        const cell = document.createElement("div");
        cell.className = "cal-month__dow";
        cell.textContent = d;
        dowRow.appendChild(cell);
      }
      grid.appendChild(dowRow);

      const days = document.createElement("div");
      days.className = "cal-month__days";

      // Previous-month blanks.
      for (let i = 0; i < startOffset; i++) {
        const blank = document.createElement("div");
        blank.className = "cal-cell cal-cell--muted";
        days.appendChild(blank);
      }

      // Current month days.
      for (let d = 1; d <= last.getDate(); d++) {
        const ymd = `${year}-${pad(month + 1)}-${pad(d)}`;
        const dow = (startOffset + d - 1) % 7;
        const isWeekend = dow >= 5;
        const isToday = ymd === today;
        const isSel = ymd === selected;
        const dayEvs = allEvents.filter((e) => e.date === ymd);

        const cell = document.createElement("button");
        cell.type = "button";
        cell.className = "cal-cell";
        if (isWeekend) cell.classList.add("cal-cell--weekend");
        if (isToday) cell.classList.add("cal-cell--today");
        if (isSel) cell.classList.add("cal-cell--selected");
        cell.dataset.date = ymd;

        const num = document.createElement("span");
        num.className = "cal-cell__num";
        num.textContent = String(d);
        cell.appendChild(num);

        const bars = dayEvs.slice(0, 3);
        const extra = dayEvs.length - bars.length;
        for (const ev of bars) {
          const meta = TYPE_BY_ID[ev.type];
          const bar = document.createElement("span");
          bar.className = "cal-event-chip";
          bar.style.background = meta.color;
          bar.dataset.eventId = ev.id;
          bar.title = ev.title;
          bar.textContent =
            ev.title.length > 18 ? `${ev.title.slice(0, 18)}…` : ev.title;
          cell.appendChild(bar);
        }
        if (extra > 0) {
          const more = document.createElement("span");
          more.className = "cal-cell__more";
          more.textContent = `+${extra} more`;
          cell.appendChild(more);
        }

        days.appendChild(cell);
      }

      grid.appendChild(days);
      gridEl.replaceChildren(grid);
    }

    function renderWeek(): void {
      const ws = getWeekStart(ref);
      const today = todayYMD();
      const days: Date[] = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(ws);
        d.setDate(d.getDate() + i);
        days.push(d);
      }
      const hours: number[] = [];
      for (let h = 7; h < 21; h++) hours.push(h);

      const wrap = document.createElement("div");
      wrap.className = "cal-week";
      const grid = document.createElement("div");
      grid.className = "cal-week__grid";

      // Header row.
      const corner = document.createElement("div");
      corner.className = "cal-week__corner";
      grid.appendChild(corner);
      for (const d of days) {
        const ymd = fmtYMD(d);
        const head = document.createElement("div");
        head.className = "cal-week__head";
        if (ymd === today) head.classList.add("cal-week__head--today");
        head.textContent = dowShortFmt.format(d);
        grid.appendChild(head);
      }

      // Hour rows.
      for (const h of hours) {
        const time = document.createElement("div");
        time.className = "cal-week__time";
        const ap = h >= 12 ? "pm" : "am";
        const h12 = h > 12 ? h - 12 : h;
        time.textContent = `${h12}${ap}`;
        grid.appendChild(time);

        for (const d of days) {
          const ymd = fmtYMD(d);
          const slot = document.createElement("button");
          slot.type = "button";
          slot.className = "cal-week__slot";
          slot.dataset.date = ymd;
          slot.dataset.hour = String(h);

          const slotEvs = allEvents.filter((e) => {
            if (e.date !== ymd || e.allDay) return false;
            const eh = e.time ? Number.parseInt(e.time.split(":")[0]!, 10) : -1;
            return eh === h;
          });
          for (const ev of slotEvs) {
            const meta = TYPE_BY_ID[ev.type];
            const bar = document.createElement("span");
            bar.className = "cal-event-chip cal-event-chip--block";
            bar.style.background = meta.color;
            bar.dataset.eventId = ev.id;
            bar.title = ev.title;
            bar.textContent = ev.title;
            slot.appendChild(bar);
          }
          grid.appendChild(slot);
        }
      }

      wrap.appendChild(grid);
      gridEl.replaceChildren(wrap);
    }

    function renderDay(): void {
      const ymd = fmtYMD(ref);
      const today = todayYMD();
      const dayEvs = allEvents.filter((e) => e.date === ymd);
      const hours: number[] = [];
      for (let h = 7; h < 21; h++) hours.push(h);

      const wrap = document.createElement("div");
      wrap.className = "cal-day";
      if (ymd === today) wrap.classList.add("cal-day--today");

      // All-day section if any.
      const allDay = dayEvs.filter((e) => e.allDay);
      if (allDay.length > 0) {
        const allWrap = document.createElement("div");
        allWrap.className = "cal-day__all-day";
        const lbl = document.createElement("span");
        lbl.className = "cal-day__all-day-label";
        lbl.textContent = "All-day";
        allWrap.appendChild(lbl);
        for (const ev of allDay) {
          const meta = TYPE_BY_ID[ev.type];
          const bar = document.createElement("span");
          bar.className = "cal-event-chip cal-event-chip--block";
          bar.style.background = meta.color;
          bar.dataset.eventId = ev.id;
          bar.textContent = ev.title;
          allWrap.appendChild(bar);
        }
        wrap.appendChild(allWrap);
      }

      const grid = document.createElement("div");
      grid.className = "cal-day__grid";
      for (const h of hours) {
        const time = document.createElement("div");
        time.className = "cal-day__time";
        const ap = h >= 12 ? "pm" : "am";
        const h12 = h > 12 ? h - 12 : h;
        time.textContent = `${h12}${ap}`;
        grid.appendChild(time);

        const slot = document.createElement("button");
        slot.type = "button";
        slot.className = "cal-day__slot";
        slot.dataset.date = ymd;
        slot.dataset.hour = String(h);

        const slotEvs = dayEvs.filter((e) => {
          if (e.allDay) return false;
          const eh = e.time ? Number.parseInt(e.time.split(":")[0]!, 10) : -1;
          return eh === h;
        });
        for (const ev of slotEvs) {
          const meta = TYPE_BY_ID[ev.type];
          const bar = document.createElement("span");
          bar.className = "cal-event-chip cal-event-chip--block";
          bar.style.background = meta.color;
          bar.dataset.eventId = ev.id;
          bar.title = ev.title;
          const timeBit = ev.time ? ` · ${fmtTime12(ev.time)}` : "";
          bar.textContent = `${ev.title}${timeBit}`;
          slot.appendChild(bar);
        }
        grid.appendChild(slot);
      }
      wrap.appendChild(grid);
      gridEl.replaceChildren(wrap);
    }

    function renderDayList(): void {
      // The "events on selected date" list. In month view it's always
      // visible — defaults to today if nothing's selected. Week / day
      // views hide it because the grid IS the list.
      if (view !== "month") {
        dayListEl.hidden = true;
        dayListItemsEl.replaceChildren();
        return;
      }
      const target = selected ?? todayYMD();
      const evs = allEvents
        .filter((e) => e.date === target)
        .sort((a, b) => (a.time || "00:00").localeCompare(b.time || "00:00"));
      const date = parseYMD(target);
      const heading = selected
        ? `Events on ${dayLongFmt.format(date)}`
        : `Today — ${dayLongFmt.format(date)}`;
      dayListTitleEl.textContent = heading;
      dayListEl.hidden = false;

      if (evs.length === 0) {
        dayListItemsEl.innerHTML = `
          <div class="cal-day-list__empty">
            No events on this date.
            <button type="button" class="cal-link" data-role="add-from-empty">Add one</button>.
          </div>
        `;
        return;
      }

      dayListItemsEl.innerHTML = evs
        .map((ev) => {
          const meta = TYPE_BY_ID[ev.type];
          const time = ev.allDay
            ? "All-day"
            : ev.time
              ? fmtTime12(ev.time) +
                (ev.endTime ? ` – ${fmtTime12(ev.endTime)}` : "")
              : "";
          const customer = ev.customer
            ? `<div class="cal-day-list__sub">${esc(ev.customer)}</div>`
            : "";
          const notes = ev.notes
            ? `<div class="cal-day-list__notes">${esc(ev.notes)}</div>`
            : "";
          const tag = ev.derived
            ? `<span class="cal-day-list__tag">from ${esc(meta.label)}</span>`
            : "";
          return `
            <article class="cal-day-list__row" data-event-id="${esc(ev.id)}">
              <span class="cal-day-list__bar" style="background:${meta.color}"></span>
              <div class="cal-day-list__body">
                <div class="cal-day-list__head">
                  <strong>${esc(ev.title)}</strong>
                  <span class="cal-day-list__time">${esc(time)}</span>
                </div>
                <div class="cal-day-list__meta">
                  <span class="cal-day-list__type" style="color:${meta.color}">${esc(meta.label)}</span>
                  ${tag}
                </div>
                ${customer}
                ${notes}
              </div>
            </article>
          `;
        })
        .join("");
    }

    function renderAll(): void {
      setActiveViewBtn();
      setTitleForView();
      if (view === "month") renderMonth();
      else if (view === "week") renderWeek();
      else renderDay();
      renderDayList();
    }

    // ── Backend ─────────────────────────────────────────────────────────────

    async function fetchUserEvents(): Promise<void> {
      // Offline-first: pull from the repo's IndexedDB cache first
      // (instant render even with no network), then queue a server
      // refresh. The repo's `change` event re-renders when fresh data
      // lands. See loadAllOffline() below for the wiring.
      if (!repo) return;
      try {
        const cached = await repo.list();
        userEvents = cached.map((r) => normaliseEvent(r, "booking"));
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[calendar] load user events failed", err);
      }
    }

    /** "Showroom visit · Jane Doe" rather than a bare "Booking · Jane Doe" —
     *  the type is the useful half at a glance. Mirrors TYPE_LABELS on the
     *  Bookings page; falls back to the raw value, then to "Booking". */
    function bookingTitle(row: Record<string, unknown>): string {
      const LABELS: Record<string, string> = {
        showroom_visit: "Showroom visit",
        design_consultation: "Design consultation",
        site_visit: "Site visit",
        callback: "Callback",
        home_visit: "Home visit",
      };
      const raw = String(row.type ?? "").trim();
      const kind = LABELS[raw] || (raw ? raw.replace(/[_-]+/g, " ") : "Booking");
      const who = String(row.customer ?? "").trim() || "Customer";
      return `${kind.charAt(0).toUpperCase()}${kind.slice(1)} · ${who}`;
    }

    async function fetchDerivedFromBookings(): Promise<CalEvent[]> {
      try {
        const resp = await ctx.api<{ bookings?: Array<Record<string, unknown>> }>(
          "/portal/bookings",
          { method: "GET", signal: ac.signal },
        );
        const out: CalEvent[] = [];
        for (const row of resp?.bookings ?? []) {
          // A customer-portal appointment request writes BOTH a booking and its
          // own calendar_events row, so deriving an event here as well showed
          // the same appointment twice — the real one, plus a generic read-only
          // "Booking · <name>" an hour out. When the API tells us a booking is
          // already on the calendar in its own right, the native event wins.
          if (row.calendarEventId) continue;
          const ev = deriveFromRow({
            id: `bk-${String(row.id ?? "")}`,
            title: bookingTitle(row),
            type: "booking",
            when: row.when ?? row.date ?? row.scheduledAt,
            notes: typeof row.notes === "string" ? row.notes : "",
            customer: typeof row.customer === "string" ? row.customer : "",
          });
          if (ev) out.push(ev);
        }
        return out;
      } catch (err) {
        if (isAbortError(err)) return [];
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.warn("[calendar] derive bookings failed", err);
        }
        return [];
      }
    }

    async function fetchDerivedFromInstallations(): Promise<CalEvent[]> {
      try {
        const resp = await ctx.api<{
          installations?: Array<Record<string, unknown>>;
        }>("/portal/installations", { method: "GET", signal: ac.signal });
        const out: CalEvent[] = [];
        for (const row of resp?.installations ?? []) {
          const ev = deriveFromRow({
            id: `in-${String(row.id ?? "")}`,
            title: `Install · ${String(row.customer ?? "Customer")}`,
            type: "installation",
            when: row.date ?? row.scheduledAt,
            customer: typeof row.customer === "string" ? row.customer : "",
          });
          if (ev) out.push(ev);
        }
        return out;
      } catch (err) {
        if (isAbortError(err)) return [];
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.warn("[calendar] derive installs failed", err);
        }
        return [];
      }
    }

    async function fetchDerivedFromDeliveries(): Promise<CalEvent[]> {
      try {
        const resp = await ctx.api<{
          deliveries?: Array<Record<string, unknown>>;
        }>("/portal/deliveries", { method: "GET", signal: ac.signal });
        const out: CalEvent[] = [];
        for (const row of resp?.deliveries ?? []) {
          const ev = deriveFromRow({
            id: `dl-${String(row.id ?? "")}`,
            title: `Delivery · ${String(row.customer ?? row.ref ?? "Customer")}`,
            type: "delivery",
            when: row.eta ?? row.date ?? row.scheduledAt,
            customer: typeof row.customer === "string" ? row.customer : "",
          });
          if (ev) out.push(ev);
        }
        return out;
      } catch (err) {
        if (isAbortError(err)) return [];
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.warn("[calendar] derive deliveries failed", err);
        }
        return [];
      }
    }

    async function fetchDerivedFromSnags(): Promise<CalEvent[]> {
      try {
        const resp = await ctx.api<{ snags?: Array<Record<string, unknown>> }>(
          "/portal/snags",
          { method: "GET", signal: ac.signal },
        );
        const out: CalEvent[] = [];
        for (const row of resp?.snags ?? []) {
          const ev = deriveFromRow({
            id: `sn-${String(row.id ?? "")}`,
            title: `Snag · ${String(row.customer ?? "Customer")}`,
            type: "snag",
            when: row.raised ?? row.date,
            notes: typeof row.issue === "string" ? row.issue : "",
            customer: typeof row.customer === "string" ? row.customer : "",
          });
          if (ev) out.push(ev);
        }
        return out;
      } catch (err) {
        if (isAbortError(err)) return [];
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.warn("[calendar] derive snags failed", err);
        }
        return [];
      }
    }

    async function fetchDerivedFromCrmFollowups(): Promise<CalEvent[]> {
      // CRM nudge follow-ups — pulled from customer_notes (migration
      // 0010) via the workflow module's aggregator endpoint. These show
      // on the calendar so the retailer sees their nudge schedule
      // alongside bookings + deliveries.
      try {
        const resp = await ctx.api<{
          followups?: Array<{ id: string; customerId: string; text: string; due: number; hours: number }>;
        }>("/portal/customer-workflow/followups", { method: "GET", signal: ac.signal });
        const out: CalEvent[] = [];
        for (const f of resp?.followups ?? []) {
          if (!f || typeof f.due !== "number" || f.due <= 0) continue;
          const d = new Date(f.due);
          const date = fmtYMD(d);
          const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
          out.push({
            id: `fu-${f.id}`,
            title: f.text || "Follow-up",
            type: "followup",
            date,
            time,
            endTime: "",
            allDay: false,
            notes: "",
            customer: "",
            derived: true,
            readOnly: true,
            recurrence: "none",
            masterId: null,
          });
        }
        return out;
      } catch (err) {
        if (isAbortError(err)) return [];
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.warn("[calendar] derive follow-ups failed", err);
        }
        return [];
      }
    }

    async function fetchDerivedFromPaymentsDue(): Promise<CalEvent[]> {
      // Payment due dates — pulled from /api/portal/payments-due (ships
      // with queue item #5). Until that endpoint exists this fetcher
      // 404s silently so the calendar still renders.
      try {
        const resp = await ctx.api<{
          payments?: Array<Record<string, unknown>>;
        }>("/portal/payments-due", { method: "GET", signal: ac.signal });
        const out: CalEvent[] = [];
        for (const row of resp?.payments ?? []) {
          const ev = deriveFromRow({
            id: `pay-${String(row["id"] ?? "")}`,
            title: `Payment due · ${String(row["customer"] ?? row["label"] ?? "")}`,
            type: "payment",
            when: row["dueDate"] ?? row["due_at"] ?? row["date"],
            notes: typeof row["notes"] === "string" ? row["notes"] : "",
            customer: typeof row["customer"] === "string" ? row["customer"] : "",
          });
          if (ev) out.push(ev);
        }
        return out;
      } catch (err) {
        if (isAbortError(err)) return [];
        if (!(err instanceof ApiError) || err.status !== 404) {
          console.warn("[calendar] derive payments due failed", err);
        }
        return [];
      }
    }

    async function fetchDerivedFromDashboardTodos(): Promise<CalEvent[]> {
      // Dashboard to-do items that have an optional due date surface here as
      // read-only 'task' events (edit/clear the date from the dashboard
      // widget). Pulls both team and personal scopes. Completed tasks stay
      // on the calendar with a ✓ prefix as a record.
      type TodoWire = { id: string; text: string; done: boolean; dueDate: string | null };
      async function fetchScope(scope: "team" | "personal"): Promise<CalEvent[]> {
        try {
          const resp = await ctx.api<{ todos?: TodoWire[] }>(
            `/portal/todos?scope=${scope}`,
            { method: "GET", signal: ac.signal },
          );
          const out: CalEvent[] = [];
          for (const t of resp?.todos ?? []) {
            if (!t || !t.dueDate || !/^\d{4}-\d{2}-\d{2}$/.test(t.dueDate)) continue;
            const text = (t.text || "Task").trim();
            out.push({
              id: `task-${String(t.id)}`,
              title: t.done ? `✓ ${text}` : text,
              type: "task",
              date: t.dueDate,
              time: "",
              endTime: "",
              allDay: true,
              notes: "",
              customer: "",
              derived: true,
              readOnly: true,
              recurrence: "none",
              masterId: null,
            });
          }
          return out;
        } catch (err) {
          if (isAbortError(err)) return [];
          if (!(err instanceof ApiError) || err.status !== 404) {
            console.warn(`[calendar] derive todos (${scope}) failed`, err);
          }
          return [];
        }
      }
      const [team, personal] = await Promise.all([
        fetchScope("team"),
        fetchScope("personal"),
      ]);
      return [...team, ...personal];
    }

    // NOTE: supplier-rep-visits source intentionally removed — retailers
    // log supplier visits as ordinary user events on the calendar instead.
    // No separate fetcher or backend feature.

    async function loadAll(): Promise<void> {
      stateEl.textContent = "Loading calendar…";
      stateEl.classList.remove("cal-state--error");
      try {
        const [, bk, ins, del, sn, fu, pm, td] = await Promise.all([
          fetchUserEvents(),
          fetchDerivedFromBookings(),
          fetchDerivedFromInstallations(),
          fetchDerivedFromDeliveries(),
          fetchDerivedFromSnags(),
          fetchDerivedFromCrmFollowups(),
          fetchDerivedFromPaymentsDue(),
          fetchDerivedFromDashboardTodos(),
        ]);
        derivedEvents = [...bk, ...ins, ...del, ...sn, ...fu, ...pm, ...td];
        recomputeAllEvents();
        stateEl.textContent = "";
        renderAll();
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[calendar] load failed", err);
        stateEl.textContent = "Could not load some events. Try again later.";
        stateEl.classList.add("cal-state--error");
        recomputeAllEvents();
        renderAll();
      }
    }

    async function initOffline(): Promise<void> {
      // Open IDB, build the queue + status + repo, mount the sync dot
      // in the toolbar, then kick off the initial load. Pattern matches
      // pages/submissions/page.ts (the reference implementation).
      try {
        db = await openDb(["calendar_events", "_sync_queue"]);
        if (disposed) return;
        const queue = createSyncQueue(db);
        syncStatus = createSyncStatus(queue);
        repo = createRepo<ServerCalRecord>({
          store: "calendar_events",
          endpoint: "/portal/calendar",
          api: rawApi,
          db,
          queue,
          parseList: parseCalendarList,
          parseOne: parseCalendarOne,
        });

        // Repo cache changes (queue drain, optimistic writes, server
        // reconciles) refresh the events list. Two important rules per
        // Steve's spec:
        //   • Do NOT call renderAll() — rebuilding the whole grid every
        //     time IDB writes flushes the user's interaction state
        //     (e.g. wipes the hover, drops focus). Only the day list
        //     re-renders here; the grid refreshes on month-nav.
        //   • Debounce so a burst of writes (initial sync drain, etc.)
        //     produces ONE re-render, not N.
        let pendingChange: ReturnType<typeof setTimeout> | null = null;
        offSubs.push(
          repo.on("change", () => {
            if (pendingChange) clearTimeout(pendingChange);
            pendingChange = setTimeout(() => {
              pendingChange = null;
              void (async (): Promise<void> => {
                if (disposed || !repo) return;
                const fresh = await repo.list();
                userEvents = fresh.map((r) => normaliseEvent(r, "booking"));
                recomputeAllEvents();
                // Day list only — preserves `selected`, doesn't rebuild
                // the grid DOM, doesn't disturb hover/focus.
                renderDayList();
              })();
            }, 300);
          }),
        );
        offSubs.push(() => {
          if (pendingChange) clearTimeout(pendingChange);
        });

        // Server-wins conflict toast.
        offSubs.push(
          repo.on("conflict", (...args: unknown[]) => {
            const payload = args[0] as RepoConflictPayload<ServerCalRecord> | undefined;
            const sv = payload?.serverVersion;
            const who = sv?.title ?? "Event";
            ctx.showToast(`${who} updated on another device — server version applied`, "info");
          }),
        );

        // Insert the sync dot just before the "+ Add event" button in
        // the title bar header (the addTopBtn lives in the page-head).
        const dot = createSyncDot({ status: syncStatus });
        dot.el.style.marginRight = "8px";
        addTopBtn.parentElement?.insertBefore(dot.el, addTopBtn);
        offSubs.push(() => dot.destroy());

        // First load — drive the syncing dot while the refresh runs.
        syncStatus.setDraining(true);
        try {
          await loadAll();
        } finally {
          syncStatus?.setDraining(false);
        }
      } catch (err) {
        console.error("[calendar] offline init failed", err);
        // Fail closed — fall back to the in-memory load path so the
        // page still renders.
        await loadAll();
      }
    }

    async function createEvent(form: NewEventForm): Promise<void> {
      // Recurrence is now a per-row rule; recurring events live as a
      // single row that's expanded into virtual instances at render
      // time (see `expandRecurringEvents`). Deleting the master row
      // removes every future occurrence — Apple Calendar style.
      if (!repo) {
        userEvents.push({
          id: genId(),
          title: form.title,
          type: form.type,
          date: form.date,
          time: form.allDay ? "" : form.time,
          endTime: form.allDay ? "" : form.endTime,
          allDay: form.allDay,
          notes: form.notes,
          customer: form.customer,
          derived: false,
          readOnly: false,
          recurrence: form.recurrence,
          masterId: null,
        });
        recomputeAllEvents();
        renderAll();
        return;
      }
      try {
        await repo.create({
          id: genId(),
          title: form.title,
          type: form.type,
          date: form.date,
          time: form.allDay ? "" : form.time,
          endTime: form.allDay ? "" : form.endTime,
          allDay: form.allDay,
          notes: form.notes,
          customer: form.customer,
          recurrence: form.recurrence,
        });
        const msg = form.recurrence === "none"
          ? "Event added"
          : `Recurring event added (${form.recurrence})`;
        ctx.showToast(msg, "success");
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[calendar] create failed", err);
        ctx.showToast("Could not save event", "error");
      }
    }

    async function updateEvent(id: string, patch: Partial<ServerCalRecord>): Promise<void> {
      if (!repo) {
        // In-memory fallback — find and mutate.
        userEvents = userEvents.map((e) => {
          if (e.id !== id) return e;
          const allDay = patch.allDay ?? e.allDay;
          return {
            ...e,
            title: patch.title ?? e.title,
            type: coerceType(patch.type ?? e.type),
            date: patch.date ?? e.date,
            time: allDay ? "" : (patch.time ?? e.time),
            endTime: allDay ? "" : (patch.endTime ?? e.endTime),
            allDay,
            customer: patch.customer ?? e.customer,
            notes: patch.notes ?? e.notes,
          };
        });
        recomputeAllEvents();
        renderAll();
        return;
      }
      try {
        await repo.update(id, patch);
        ctx.showToast("Event updated", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("[calendar] update failed", err);
        ctx.showToast("Could not save changes", "error");
      }
    }

    async function deleteEvent(id: string): Promise<void> {
      // Virtual recurrence instances carry the master id via `masterId`;
      // the caller hands us the master id directly, so this just deletes
      // one row by id (the master). All future virtual instances vanish
      // because expansion no longer finds the master.
      if (!repo) {
        userEvents = userEvents.filter((e) => e.id !== id);
        recomputeAllEvents();
        renderAll();
        return;
      }
      // Let success/error toasts be owned by the caller (the event-detail
      // modal), which surfaces the server's real error message and avoids the
      // old double-toast (this handler showed "Could not delete" while the
      // modal still reported success because the error was swallowed here).
      await repo.remove(id);
    }

    // ── Form submit ─────────────────────────────────────────────────────────

    function handleSubmit(e: Event): void {
      e.preventDefault();
      const title = titleInput.value.trim();
      if (!title) {
        titleInput.focus();
        return;
      }
      const date = dateInput.value;
      if (!date) {
        dateInput.focus();
        return;
      }
      const allDay = allDayInput.checked;
      const time = allDay ? "" : timeInput.value;
      const endTime = allDay ? "" : endTimeInput.value;

      const rawRec = recurrenceInput.value as Recurrence;
      const recurrence: Recurrence =
        rawRec === "daily" || rawRec === "weekly" ||
        rawRec === "monthly" || rawRec === "yearly"
          ? rawRec
          : "none";

      const form: NewEventForm = {
        title,
        type: coerceType(typeInput.value),
        date,
        time,
        endTime,
        allDay,
        notes: notesInput.value.trim(),
        customer: customerSelection?.name ?? "",
        recurrence,
      };

      saveBtn.disabled = true;
      saveBtn.textContent = "Saving…";
      void createEvent(form).finally(() => {
        saveBtn.disabled = false;
        saveBtn.textContent = "Save event";
        closeModal();
      });
    }

    // ── Wiring ──────────────────────────────────────────────────────────────

    function navigate(delta: number): void {
      if (view === "month") {
        ref = new Date(ref.getFullYear(), ref.getMonth() + delta, 1);
      } else if (view === "week") {
        ref = new Date(ref);
        ref.setDate(ref.getDate() + delta * 7);
      } else {
        ref = new Date(ref);
        ref.setDate(ref.getDate() + delta);
      }
      // If we navigate out of a selected date's month, clear selection.
      if (view === "month" && selected) {
        const sel = parseYMD(selected);
        if (
          sel.getFullYear() !== ref.getFullYear() ||
          sel.getMonth() !== ref.getMonth()
        ) {
          selected = null;
        }
      }
      // Re-expand recurring events for the new visible window so
      // virtual instances appear in the freshly-navigated month.
      recomputeAllEvents();
      renderAll();
    }

    function goToday(): void {
      ref = new Date();
      selected = todayYMD();
      recomputeAllEvents();
      renderAll();
    }

    function setView(v: CalView): void {
      view = v;
      recomputeAllEvents();
      renderAll();
    }

    const onPrev = (): void => navigate(-1);
    const onNext = (): void => navigate(1);
    const onToday = (): void => goToday();
    const onAddTop = (): void => openModal(selected ?? todayYMD());

    prevBtn.addEventListener("click", onPrev);
    nextBtn.addEventListener("click", onNext);
    todayBtn.addEventListener("click", onToday);
    addTopBtn.addEventListener("click", onAddTop);

    const onViewBtnClick = (e: MouseEvent): void => {
      const btn = e.currentTarget as HTMLButtonElement;
      const v = btn.dataset.view as CalView | undefined;
      if (v === "month" || v === "week" || v === "day") setView(v);
    };
    for (const btn of viewBtns) btn.addEventListener("click", onViewBtnClick);

    // Click in the grid: select a month-view cell, or open the Add-Event
    // modal pre-filled to that cell / week-slot / day-slot.
    const onGridClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Event chip click: surface the day in the list panel below the
      // grid (the user can read full details there). Don't toast — the
      // panel IS the surface for event details. Then fall through to
      // the cell-selection branch so `selected` updates.
      const chip = target.closest<HTMLElement>("[data-event-id]");
      if (chip) {
        e.stopPropagation();
      }

      const cell = target.closest<HTMLElement>(".cal-cell");
      if (cell && view === "month") {
        const ymd = cell.dataset.date;
        if (!ymd) return;
        if (selected === ymd && !chip) {
          // Second click on the empty cell area: open Add-Event for
          // that date. Chip clicks never trigger this — they just
          // re-select the day so the list re-renders.
          openModal(ymd);
        } else {
          // Update selected and re-render the day list IN PLACE without
          // rebuilding the entire month grid. The grid rebuild was
          // causing visible flicker (DOM destroyed → hover state lost
          // → mouse re-acquires hover on the new cell). Swap the
          // `cal-cell--selected` class on the affected cells directly.
          const prevYmd = selected;
          selected = ymd;
          if (prevYmd) {
            const prev = gridEl.querySelector<HTMLElement>(
              `.cal-cell[data-date="${prevYmd}"]`,
            );
            prev?.classList.remove("cal-cell--selected");
          }
          cell.classList.add("cal-cell--selected");
          renderDayList();
        }
        return;
      }

      const weekSlot = target.closest<HTMLElement>(".cal-week__slot");
      if (weekSlot) {
        const ymd = weekSlot.dataset.date;
        if (ymd) openModal(ymd);
        return;
      }

      const daySlot = target.closest<HTMLElement>(".cal-day__slot");
      if (daySlot) {
        const ymd = daySlot.dataset.date;
        if (ymd) openModal(ymd);
        return;
      }
    };
    gridEl.addEventListener("click", onGridClick);

    // Day-list "Add one" link + row click → detail popup.
    const onDayListClick = (e: MouseEvent): void => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const link = t.closest<HTMLButtonElement>(
        "[data-role='add-from-empty']",
      );
      if (link && selected) {
        openModal(selected);
        return;
      }
      const row = t.closest<HTMLElement>("[data-event-id]");
      if (row) {
        const id = row.dataset.eventId;
        if (!id) return;
        const ev = allEvents.find((x) => x.id === id);
        if (ev) openDetail(ev);
      }
    };
    dayListEl.addEventListener("click", onDayListClick);

    // Modal wiring.
    formEl.addEventListener("submit", handleSubmit);
    cancelBtn.addEventListener("click", () => closeModal());
    closeBtn.addEventListener("click", () => closeModal());
    allDayInput.addEventListener("change", syncAllDayUI);

    const onDialogClick = (e: MouseEvent): void => {
      if (e.target === dialog) closeModal();
    };
    dialog.addEventListener("click", onDialogClick);

    // Keyboard nav: ←/→ to step through periods, T for today.
    const onKeyDown = (e: KeyboardEvent): void => {
      // Don't hijack typing in inputs (especially the modal).
      const tag = (e.target as HTMLElement | null)?.tagName ?? "";
      if (
        tag === "INPUT" ||
        tag === "SELECT" ||
        tag === "TEXTAREA" ||
        dialog.open
      ) {
        return;
      }
      if (e.key === "ArrowLeft") {
        navigate(-1);
      } else if (e.key === "ArrowRight") {
        navigate(1);
      } else if (e.key === "t" || e.key === "T") {
        goToday();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    // ── Initial render & load ──────────────────────────────────────────────

    renderAll();
    void initOffline();

    // Open Add Event pre-filled if the caller deep-linked with ?add=1
    // (dashboard's calendar widget "+ Add event" button does this).
    if (paramShouldAdd) {
      window.setTimeout(() => openModal(selected ?? todayYMD()), 80);
    }

    // ── Cleanup ─────────────────────────────────────────────────────────────

    return () => {
      disposed = true;
      ac.abort();
      for (const off of offSubs) {
        try { off(); } catch (err) { console.error("[calendar] cleanup", err); }
      }
      offSubs.length = 0;
      syncStatus?.destroy();
      syncStatus = null;
      repo = null;
      db = null;

      prevBtn.removeEventListener("click", onPrev);
      nextBtn.removeEventListener("click", onNext);
      todayBtn.removeEventListener("click", onToday);
      addTopBtn.removeEventListener("click", onAddTop);
      for (const btn of viewBtns) {
        btn.removeEventListener("click", onViewBtnClick);
      }
      gridEl.removeEventListener("click", onGridClick);
      dayListEl.removeEventListener("click", onDayListClick);
      formEl.removeEventListener("submit", handleSubmit);
      allDayInput.removeEventListener("change", syncAllDayUI);
      dialog.removeEventListener("click", onDialogClick);
      document.removeEventListener("keydown", onKeyDown);

      if (dialog.open) dialog.close();
    };
  },
};

registerPage(calendarPage);

export default calendarPage;
